import { green, grey, red } from "@ant-design/colors"
import { faPaperPlane, faPause, faThumbsUp, faXmarkToSlot } from "@fortawesome/pro-duotone-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Card, Space, Table } from "antd"
import { ProjectType, projectTypeName } from "../../../common/constants"
import { getProjectUrl } from "../datacenterModel"
import { ItemReportDataAggregated } from "./itemsReportModel"
import { getItemsExportRows, itemsExportColumns } from "./itemsReportExport"
import ExportCsvButton from "../ExportCsvButton"
import useDashboardReporting from "../../../hooks/useDashboardReporting"
import { CsvDateRange } from "../reportCsvExport"

type Props = {
  items: ItemReportDataAggregated[]
  dateRange: CsvDateRange
}

const ItemsReportItemsList = ({ items, dateRange }: Props) => {
  const dashboardReporting = useDashboardReporting()
  return <Card extra={dashboardReporting && <ExportCsvButton reportName='Items Usage' dateRange={dateRange} columns={itemsExportColumns} rows={getItemsExportRows(items)} />}>
    <Table
      dataSource={items}
      rowKey={(record) => `${record.name}|${record.units}`}
      pagination={{ showSizeChanger: true }}
      columns={[
        {
          title: 'Item',
          dataIndex: 'name',
          sorter: (a, b) => a.name.localeCompare(b.name),
        },
        {
          title: 'Estimated Quantity',
          dataIndex: 'estimatedQuantity',
          sorter: (a, b) => a.estimatedQuantity - b.estimatedQuantity,
          render: (_, record) => `${record.estimatedQuantity.toFixed(2)} ${record.units}`
        },
        {
          title: 'Actual Quantity',
          dataIndex: 'actualQuantity',
          sorter: (a, b) => a.actualQuantity - b.actualQuantity,
          render: (_, record) => `${record.actualQuantity.toFixed(2)} ${record.units}`
        }
      ]}
      expandable={{
        expandedRowRender: record => <ExpandedComponent record={record} />
      }}
    />
  </Card>
}

export default ItemsReportItemsList

const ExpandedComponent = ({ record }: { record: ItemReportDataAggregated }) => (
  <Card title="Related Projects">
    <Table
      dataSource={record.relatedProjects}
      rowKey={(record) => `${record.projectId}|${record.workareaName}`}
      pagination={false}
      columns={[
        { title: 'Project', dataIndex: 'projectName' },
        { title: 'Customer', dataIndex: 'customerDisplayName' },
        { title: 'Workarea', dataIndex: 'workareaName' },
        { title: 'Estimated Quantity', dataIndex: 'estimatedQuantity', render: (_, record) => `${record.estimatedQuantity.toFixed(2)}` },
        { title: 'Actual Quantity', dataIndex: 'actualQuantity', render: (_, record) => `${record.actualQuantity.toFixed(2)}` },
        { title: 'Job Type', dataIndex: 'jobType', render: (jobType: ProjectType) => projectTypeName(jobType) },
        { title: 'Status', dataIndex: 'status', render: (_, record) => <Status approved={record.isApproved} lost={record.lost} onHold={record.isOnHold} /> }
      ]}
      onRow={(record) => {
        const jobUrl = getProjectUrl(record.projectId, record.jobType, record.sold, record.projectName)
        return {
          onClick: () => window.open(`#!/${jobUrl}`, '_blank'),
          style: { cursor: 'pointer' }
        }
      }}
    />
  </Card>)

const Status = ({ approved, onHold, lost }: { approved: boolean, onHold: boolean, lost: boolean }) => {
  if (lost) {
    return <Space>
      <FontAwesomeIcon icon={faXmarkToSlot} color={red.primary} size='xl' />
      Lost
    </Space>
  }

  if (onHold) {
    return <Space>
      <FontAwesomeIcon icon={faPause} color={grey[0]} size='xl' />
      On Hold
    </Space>
  }

  if (approved) {
    return <Space>
      <FontAwesomeIcon icon={faThumbsUp} color={green.primary} size='xl' />
      Approved
    </Space>
  }

  return <Space>
    <FontAwesomeIcon icon={faPaperPlane} color={grey[0]} size='xl' />
    Draft
  </Space>
}